import { createContext, useContext, useMemo } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  useUser,
  useClerk,
  useOrganization
} from '@clerk/clerk-react'
import V2Layout from './layout/V2Layout'

// Lazy-loaded pages
import V2LoginPage from './auth/V2LoginPage'
import V2SignupPage from './auth/V2SignupPage'

import V2OnboardingWizard from './onboarding/V2OnboardingWizard'
import V2Dashboard from './dashboard/V2Dashboard'
import V2ProviderList from './providers/V2ProviderList'
import V2ShipmentList from './shipments/V2ShipmentList'
import V2ShipmentForm from './shipments/V2ShipmentForm'
import V2ShipmentDetail from './shipments/V2ShipmentDetail'
import V2QuoteComparison from './quotes/V2QuoteComparison'
import V2QuoteDetail from './quotes/V2QuoteDetail'
import V2SettingsPage from './settings/V2SettingsPage'
import NewQuotePage from './quote/NewQuotePage'
import V2WorkspacePage from './workspace/V2WorkspacePage'
import V2QuoteExplorer from './analytics/V2QuoteExplorer'

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// ---------------------------------------------------------------------------
// V2 Auth Context — compatibility shim.
//
// Existing V2 components (V2Layout, V2Sidebar, …) call `useV2Auth()` and
// expect `{ user, login, logout }`. Rather than refactor every consumer,
// we keep the same shape but populate it from Clerk's hooks. Components
// keep working unchanged; Clerk owns real authentication under the hood.
// ---------------------------------------------------------------------------
const V2AuthContext = createContext(null)

export function useV2Auth() {
  const ctx = useContext(V2AuthContext)
  if (!ctx) throw new Error('useV2Auth must be inside V2App')
  return ctx
}

function buildInitials(user) {
  if (!user) return 'U'
  const first = user.firstName?.[0] || ''
  const last = user.lastName?.[0] || ''
  const fromName = (first + last).toUpperCase()
  if (fromName) return fromName
  const email = user.primaryEmailAddress?.emailAddress || ''
  return email ? email[0].toUpperCase() : 'U'
}

function V2AuthProvider({ children }) {
  const { user: clerkUser, isSignedIn } = useUser()
  const { organization } = useOrganization()
  const clerk = useClerk()
  const navigate = useNavigate()

  const value = useMemo(() => {
    const mapped = isSignedIn && clerkUser ? {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      name: clerkUser.fullName || clerkUser.firstName || '',
      avatarInitials: buildInitials(clerkUser),
      company: organization?.name || ''
    } : null

    return {
      user: mapped,
      login: () => navigate('/login'),
      signup: () => navigate('/signup'),
      logout: () => clerk.signOut(() => navigate('/login'))
    }
  }, [clerkUser, isSignedIn, clerk, navigate, organization])

  return (
    <V2AuthContext.Provider value={value}>
      {children}
    </V2AuthContext.Provider>
  )
}

/** Route guard. Authenticated users pass through; anonymous users land on /login. */
function V2ProtectedRoute({ children }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Navigate to="/login" replace />
      </SignedOut>
    </>
  )
}

function V2Routes() {
  return (
    <V2AuthProvider>
      <Routes>
        {/* Public routes — root redirects to login */}
        <Route index element={<Navigate to="/login" replace />} />
        <Route path="login" element={<V2LoginPage />} />
        <Route path="signup" element={<V2SignupPage />} />
        <Route path="onboarding" element={<V2OnboardingWizard />} />

        {/* Authenticated routes with tenant sidebar layout */}
        <Route element={
          <V2ProtectedRoute>
            <V2Layout />
          </V2ProtectedRoute>
        }>
          <Route path="dashboard" element={<V2Dashboard />} />
          <Route path="providers" element={<V2ProviderList />} />
          {/* Workspace — merged shipment+chat surface */}
          <Route path="workspace" element={<V2WorkspacePage />} />
          <Route path="workspace/:id" element={<V2WorkspacePage />} />
          {/* Legacy redirects — Chat and Shipments now live inside Workspace */}
          <Route path="shipments" element={<Navigate to="/workspace" replace />} />
          <Route path="chat" element={<Navigate to="/workspace" replace />} />
          <Route path="chat-preview" element={<Navigate to="/workspace" replace />} />
          {/* Keep deep-link routes for shipment details and new quote form */}
          <Route path="shipments/new" element={<NewQuotePage />} />
          <Route path="shipments/:id" element={<V2ShipmentDetail />} />
          <Route path="shipments/:id/quotes/:quoteId" element={<V2QuoteDetail />} />
          <Route path="shipments/:id/compare" element={<V2QuoteComparison />} />
          <Route path="analytics" element={<V2QuoteExplorer />} />
          <Route path="settings" element={<V2SettingsPage />} />
          <Route path="quote/new" element={<NewQuotePage />} />
          {/* Legacy /v2/billing → redirect to Settings (billing is sales-driven, managed by platform admin) */}
          <Route path="billing" element={<Navigate to="/settings" replace />} />
        </Route>
      </Routes>
    </V2AuthProvider>
  )
}

export default function V2App() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div style={{
        maxWidth: 600,
        margin: '4rem auto',
        padding: '1.5rem',
        border: '1px solid #8B3A3A',
        background: 'rgba(139,58,58,0.08)',
        borderRadius: 8,
        fontFamily: 'system-ui, sans-serif'
      }}>
        <h2 style={{ marginTop: 0, color: '#8B3A3A' }}>Clerk not configured</h2>
        <p>Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in the frontend env and restart the dev server.</p>
        <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>
          Get the key at <a href="https://dashboard.clerk.com">dashboard.clerk.com</a> → API Keys.
        </p>
      </div>
    )
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/onboarding"
    >
      <V2Routes />
    </ClerkProvider>
  )
}
